import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { getAllPages, createPage, updatePage, deletePage } from '../../services/pageService';
import { Page, CreatePageData, UpdatePageData } from '../../types/page';

// تم إزالة تكوين محرر النصوص المتقدم

const PagesManagementPage: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState<Page | null>(null);
  const [formData, setFormData] = useState<CreatePageData>({
    title: '',
    slug: '',
    content: '',
    metaTitle: '',
    metaDescription: '',
    isActive: true,
    showInFooter: false,
    showInHeader: false,
    footerSection: 'none',
    order: 0
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // تحميل الصفحات عند تحميل المكون
  useEffect(() => {
    loadPages();
  }, []);

  // تحميل الصفحات من قاعدة البيانات
  const loadPages = async () => {
    try {
      setLoading(true);
      const pagesData = await getAllPages();
      setPages(pagesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading pages:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل الصفحات',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // التحقق من صحة البيانات
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'عنوان الصفحة مطلوب';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'الرابط المختصر مطلوب';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'الرابط المختصر يجب أن يحتوي على أحرف إنجليزية صغيرة وأرقام وشرطات فقط';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'محتوى الصفحة مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // فتح نافذة إضافة صفحة جديدة
  const handleAddPage = () => {
    setCurrentPage(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      metaTitle: '',
      metaDescription: '',
      isActive: true,
      showInFooter: false,
      showInHeader: false,
      footerSection: 'none',
      order: pages.length + 1
    });
    setErrors({});
    setOpenDialog(true);
  };

  // فتح نافذة تعديل صفحة
  const handleEditPage = (page: Page) => {
    setCurrentPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      metaTitle: page.metaTitle || '',
      metaDescription: page.metaDescription || '',
      isActive: page.isActive,
      showInFooter: page.showInFooter,
      showInHeader: page.showInHeader,
      footerSection: page.footerSection || 'none',
      order: page.order
    });
    setErrors({});
    setOpenDialog(true);
  };

  // فتح نافذة حذف صفحة
  const handleDeleteClick = (page: Page) => {
    setCurrentPage(page);
    setOpenDeleteDialog(true);
  };

  // حذف صفحة
  const handleDeleteConfirm = async () => {
    if (!currentPage) return;

    try {
      await deletePage(currentPage.id);
      setPages(pages.filter(p => p.id !== currentPage.id));
      setSnackbar({
        open: true,
        message: 'تم حذف الصفحة بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting page:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الصفحة',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setCurrentPage(null);
    }
  };

  // تغيير ترتيب الصفحة
  const handleChangeOrder = async (page: Page, direction: 'up' | 'down') => {
    const newPages = [...pages];
    const index = newPages.findIndex(p => p.id === page.id);

    if (direction === 'up' && index > 0) {
      // تبديل الصفحة مع الصفحة السابقة
      const temp = newPages[index - 1].order;
      newPages[index - 1].order = newPages[index].order;
      newPages[index].order = temp;

      // تحديث الصفحتين في قاعدة البيانات
      try {
        await updatePage(newPages[index - 1].id, { order: newPages[index - 1].order });
        await updatePage(newPages[index].id, { order: newPages[index].order });

        // إعادة ترتيب المصفوفة
        newPages.sort((a, b) => a.order - b.order);
        setPages(newPages);

        setSnackbar({
          open: true,
          message: 'تم تغيير ترتيب الصفحة بنجاح',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error updating page order:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تغيير ترتيب الصفحة',
          severity: 'error'
        });
      }
    } else if (direction === 'down' && index < newPages.length - 1) {
      // تبديل الصفحة مع الصفحة التالية
      const temp = newPages[index + 1].order;
      newPages[index + 1].order = newPages[index].order;
      newPages[index].order = temp;

      // تحديث الصفحتين في قاعدة البيانات
      try {
        await updatePage(newPages[index + 1].id, { order: newPages[index + 1].order });
        await updatePage(newPages[index].id, { order: newPages[index].order });

        // إعادة ترتيب المصفوفة
        newPages.sort((a, b) => a.order - b.order);
        setPages(newPages);

        setSnackbar({
          open: true,
          message: 'تم تغيير ترتيب الصفحة بنجاح',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error updating page order:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تغيير ترتيب الصفحة',
          severity: 'error'
        });
      }
    }
  };

  // تغيير حالة نشاط الصفحة
  const handleToggleActive = async (page: Page) => {
    try {
      await updatePage(page.id, { isActive: !page.isActive });
      setPages(pages.map(p => p.id === page.id ? { ...p, isActive: !p.isActive } : p));
      setSnackbar({
        open: true,
        message: `تم ${!page.isActive ? 'تفعيل' : 'إلغاء تفعيل'} الصفحة بنجاح`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating page status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة الصفحة',
        severity: 'error'
      });
    }
  };

  // حفظ الصفحة (إضافة أو تعديل)
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (currentPage) {
        // تعديل صفحة موجودة
        await updatePage(currentPage.id, formData as UpdatePageData);
        setPages(pages.map(p => p.id === currentPage.id ? { ...p, ...formData, updatedAt: new Date() } : p));
        setSnackbar({
          open: true,
          message: 'تم تحديث الصفحة بنجاح',
          severity: 'success'
        });
      } else {
        // إضافة صفحة جديدة
        const newPageId = await createPage(formData);
        const newPage: Page = {
          id: newPageId,
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setPages([...pages, newPage]);
        setSnackbar({
          open: true,
          message: 'تم إنشاء الصفحة بنجاح',
          severity: 'success'
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving page:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء ${currentPage ? 'تحديث' : 'إنشاء'} الصفحة`,
        severity: 'error'
      });
    }
  };

  // تغيير قيمة حقل في النموذج
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // إذا كان الحقل هو العنوان وكان حقل الرابط المختصر فارغًا، قم بإنشاء رابط مختصر تلقائيًا
    if (name === 'title' && !formData.slug) {
      const slug = value
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // إزالة الأحرف الخاصة
        .replace(/\s+/g, '-') // استبدال المسافات بشرطات
        .replace(/--+/g, '-') // استبدال الشرطات المتعددة بشرطة واحدة
        .trim();
      setFormData({ ...formData, slug });
    }

    // إذا كان الحقل هو العنوان وكان حقل عنوان الميتا فارغًا، قم بنسخ العنوان إلى عنوان الميتا
    if (name === 'title' && !formData.metaTitle) {
      setFormData({ ...formData, metaTitle: value });
    }
  };

  // تم إزالة دالة handleContentChange لأننا نستخدم handleChange بدلاً منها

  // تغيير قيمة الخيارات (Switch)
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  // تغيير قيمة قسم الفوتر
  const handleFooterSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData({ ...formData, footerSection: value as 'quick_links' | 'information' | 'none' });
  };

  // إغلاق رسالة الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Grid container spacing={2} alignItems="center" mb={4}>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              إدارة الصفحات
            </Typography>
            <Typography variant="body1" color="text.secondary">
              إدارة الصفحات الثابتة مثل "من نحن" و"اتصل بنا" وغيرها
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddPage}
            >
              إضافة صفحة جديدة
            </Button>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : pages.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                لا توجد صفحات حالياً
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddPage}
                sx={{ mt: 2 }}
              >
                إضافة صفحة جديدة
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>الترتيب</TableCell>
                    <TableCell>العنوان</TableCell>
                    <TableCell>الرابط المختصر</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الظهور</TableCell>
                    <TableCell>آخر تحديث</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pages.map((page) => (
                    <TableRow key={page.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {page.order}
                          </Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleChangeOrder(page, 'up')}
                              disabled={pages.indexOf(page) === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleChangeOrder(page, 'down')}
                              disabled={pages.indexOf(page) === pages.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{page.title}</TableCell>
                      <TableCell>{page.slug}</TableCell>
                      <TableCell>
                        <Chip
                          label={page.isActive ? 'نشط' : 'غير نشط'}
                          color={page.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {page.showInHeader && (
                            <Chip label="القائمة" color="primary" size="small" />
                          )}
                          {page.showInFooter && (
                            <Chip
                              label={
                                page.footerSection === 'quick_links'
                                  ? 'روابط سريعة'
                                  : page.footerSection === 'information'
                                  ? 'معلومات'
                                  : 'الفوتر'
                              }
                              color="secondary"
                              size="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {page.updatedAt.toLocaleDateString('ar-EG')}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="معاينة">
                            <IconButton
                              color="info"
                              onClick={() => window.open(`/${page.slug}`, '_blank')}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={page.isActive ? 'إلغاء التفعيل' : 'تفعيل'}>
                            <IconButton
                              color={page.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleActive(page)}
                            >
                              {page.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تعديل">
                            <IconButton
                              color="primary"
                              onClick={() => handleEditPage(page)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(page)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* نافذة إضافة/تعديل صفحة */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentPage ? 'تعديل صفحة' : 'إضافة صفحة جديدة'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="title"
                label="عنوان الصفحة"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!errors.title}
                helperText={errors.title}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="slug"
                label="الرابط المختصر"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!errors.slug}
                helperText={errors.slug || 'مثال: about-us (يستخدم في عنوان URL)'}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="content"
                label="محتوى الصفحة"
                value={formData.content}
                onChange={handleChange}
                fullWidth
                multiline
                rows={10}
                margin="normal"
                error={!!errors.content}
                helperText={errors.content}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                إعدادات SEO
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="metaTitle"
                label="عنوان الميتا (Meta Title)"
                value={formData.metaTitle}
                onChange={handleChange}
                fullWidth
                margin="normal"
                helperText="عنوان الصفحة الذي يظهر في نتائج البحث وعلامة تبويب المتصفح"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="metaDescription"
                label="وصف الميتا (Meta Description)"
                value={formData.metaDescription}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                helperText="وصف موجز للصفحة يظهر في نتائج البحث"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                إعدادات العرض
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="الصفحة نشطة"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="order"
                label="الترتيب"
                type="number"
                value={formData.order}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showInHeader}
                    onChange={handleSwitchChange}
                    name="showInHeader"
                    color="primary"
                  />
                }
                label="إظهار في القائمة العلوية"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showInFooter}
                    onChange={handleSwitchChange}
                    name="showInFooter"
                    color="primary"
                  />
                }
                label="إظهار في الفوتر"
              />
            </Grid>
            {formData.showInFooter && (
              <Grid item xs={12}>
                <TextField
                  select
                  name="footerSection"
                  label="قسم الفوتر"
                  value={formData.footerSection}
                  onChange={handleFooterSectionChange}
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="none">بدون قسم محدد</MenuItem>
                  <MenuItem value="quick_links">روابط سريعة</MenuItem>
                  <MenuItem value="information">معلومات</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة تأكيد الحذف */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من حذف صفحة "{currentPage?.title}"؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة الإشعار */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PagesManagementPage;
